/**
 * SaaAnalysisModal — full-page (full-screen) view of ONE live SAA analysis, used from the
 * MiFID/AQ questionnaire (#4 Inc 3, 2026-09-13) in place of the old static chart images.
 *
 * Defaults per Pedro: ALL SAA profiles, FULL history. Each of the questionnaire's buttons
 * opens one element — Statistics table / Cumulative return / Drawdown / Return-vs-max-drawdown.
 * SaaAnalyticsView is React.lazy'd so the ~840KB Vega family stays off the AQ page bundle
 * until a chart is actually opened (C-260).
 */
import { lazy, Suspense } from 'react';
import { Alert, Group, Loader, Modal } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { saaApi } from '../api/saa';
import type { SaaSection } from './SaaAnalyticsView';

const SaaAnalyticsView = lazy(() => import('./SaaAnalyticsView'));

const TITLES: Record<SaaSection, string> = {
  stats: 'SAA — Statistics',
  q8fit: 'SAA — Fit with Q8 (tolerance to devaluations over 1 year)',
  cumulative: 'SAA — Cumulative return',
  drawdown: 'SAA — Drawdown',
  volScatter: 'SAA — Return vs risk (volatility)',
  ddScatter: 'SAA — Return vs drawdown',
  monthly: 'SAA — Monthly returns',
  outcomes: 'SAA — Range of outcomes',
  rolling: 'SAA — Rolling returns',
  bracket: 'SAA — Average returns, bracketed by the best and worst',
};

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');

export function SaaAnalysisModal({ section, onClose }: { section: SaaSection | null; onClose: () => void }) {
  // All profiles, in canonical order; analytics over each profile's full history (no dates).
  const profilesQ = useQuery({ queryKey: ['saa', 'profiles'], queryFn: () => saaApi.profiles() });
  const codes = (profilesQ.data ?? []).map((p) => p.code);

  const analyticsQ = useQuery({
    queryKey: ['saa', 'analytics', 'aq', codes],
    queryFn: () => saaApi.returnsAnalytics(codes),
    enabled: section !== null && codes.length > 0,
  });

  return (
    <Modal opened={section !== null} onClose={onClose} fullScreen title={section ? TITLES[section] : ''}>
      {(profilesQ.isLoading || analyticsQ.isLoading) && (
        <Group justify="center" p="xl"><Loader /></Group>
      )}
      {analyticsQ.isError && <Alert color="red" title="Could not load the analysis">{errMsg(analyticsQ.error)}</Alert>}
      {section && analyticsQ.data && (
        <Suspense fallback={<Group justify="center" p="xl"><Loader /></Group>}>
          <SaaAnalyticsView
            profiles={analyticsQ.data.profiles}
            orderedCodes={codes}
            only={[section]}
            variant="plain"
          />
        </Suspense>
      )}
    </Modal>
  );
}
